import React, { useState, useEffect } from "react";

import { API, graphqlOperation } from "aws-amplify";
import { countInboundShipments } from "../../../graphql/inbound/custom-queries";

import { addDays, format, startOfDay, startOfMonth, sub } from "date-fns";

import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Box, Typography } from "@material-ui/core";

import { useNotify } from "ra-core";

import { logError } from "../../../utils/appUtils";
import { InboundStatus } from "../../../utils/constants";
import { BackButton } from "../../../components/common/BackButton";
import { ForceTitle } from "../../../components/common/ForceTitle";

const DEFAULT_DAYS = 31;
const emptyData = Array.from({ length: DEFAULT_DAYS }, () => ({}));

const CustomizedLabel = (props) => {
  const { x, y, fill, value } = props;

  return (
    <text
      x={x}
      y={y}
      dy={-4}
      fontSize="16"
      fontFamily="sans-serif"
      fill={fill}
      textAnchor="middle"
    >
      {value}
    </text>
  );
};

const DailyInboundsChart = ({ reloadYardId, location, ...props }) => {
  const notify = useNotify();
  const [data, setData] = useState(emptyData);
  const [average, setAverage] = useState(0);

  useEffect(() => {
    getInboundCarsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const getInboundCarsData = async () => {
    const params = new URLSearchParams(location?.search || "");
    const monthStartParam = params.get("monthStart");

    let startDate;
    let tempData = [];

    try {
      const queryList = [];
      const yardId = reloadYardId ? reloadYardId : window.appUser?.reloadYardId;
      debugger;

      if (monthStartParam) {
        // Full month view starting from the given monthStart
        const monthStartDate = startOfMonth(new Date(monthStartParam));
        startDate = monthStartDate;
        for (let i = 0; ; i++) {
          const newDate = addDays(startDate, i);
          if (newDate.getMonth() !== monthStartDate.getMonth()) break;

          const nextDate = addDays(newDate, 1);
          const dayName = format(newDate, "d");
          const options = {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
          };
          const barDate = newDate.toLocaleDateString("en-US", options);

          tempData[i] = { name: dayName, date: barDate, amt: 0 };

          queryList[i] = API.graphql(
            graphqlOperation(countInboundShipments, {
              filter: {
                reloadYardId: { eq: yardId },
                status: { eq: InboundStatus.RELEASED },
                and: [
                  { releaseDateTime: { gte: newDate.toISOString() } },
                  { releaseDateTime: { lt: nextDate.toISOString() } },
                ],
              },
              limit: 1000,
            })
          );
        }
      } else {
        // Default: last 14 days
        startDate = startOfDay(sub(new Date(), { days: 14 }));
        for (let i = 0; i <= 13; i++) {
          const newDate = addDays(startDate, i);
          const nextDate = addDays(newDate, 1);
          const dayName = format(newDate, "EEE");
          const options = {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
          };
          const barDate = newDate.toLocaleDateString("en-US", options);

          tempData[i] = { name: dayName, date: barDate, amt: 0 };

          queryList[i] = API.graphql(
            graphqlOperation(countInboundShipments, {
              filter: {
                reloadYardId: { eq: yardId },
                status: { eq: InboundStatus.RELEASED },
                and: [
                  { releaseDateTime: { gte: newDate.toISOString() } },
                  { releaseDateTime: { lt: nextDate.toISOString() } },
                ],
              },
              limit: 1000,
            })
          );
        }
      }

      const promiseResults = await Promise.all(queryList);

      let total = 0;
      let length = 0;
      promiseResults.forEach((result, idx) => {
        const amt = result.data.searchInboundShipments.total;
        tempData[idx].amt = amt;
        total += amt || 0;
        length += amt != null ? 1 : 0;
      });

      const avg = length > 0 ? Math.round(total / length) : 0;

      setData(tempData);
      setAverage(avg);
    } catch (err) {
      logError("DailyInbounds:getInboundCarsData", err, notify);
    }
  };

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <ForceTitle title="Daily Inbounds" />
        <BackButton basePath="/reports" />
      </Box>
      <ResponsiveContainer>
        <BarChart
          data={data}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" tickSize dy="25" />
          <YAxis />
          <Tooltip
            content={({ payload }) => {
              if (payload && payload.length) {
                const { date, amt } = payload[0].payload;
                return (
                  <div
                    style={{
                      color: "black",
                      background: "white",
                      padding: "8px",
                      border: "1px solid black",
                    }}
                  >
                    <Typography>{`Date: ${date}`}</Typography>
                    <Typography>{`Value: ${amt}`}</Typography>
                    <Typography>{`Average: ${average}`}</Typography>
                  </div>
                );
              }
              return null;
            }}
          />
          {average && (
            <ReferenceLine
              y={average}
              stroke="red"
              strokeWidth={6}
              strokeDasharray="6 6"
            />
          )}
          <Bar dataKey="amt" stroke="blue" label={<CustomizedLabel />} />
        </BarChart>
      </ResponsiveContainer>
    </>
  );
};

export default DailyInboundsChart;
